import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AllergiesRepositoryService,
  Allergy,
  Events,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';

/**
 * PatientAllergiesStore — list of allergies for the current patient.
 *
 * Auto-loads when the current patient changes, clears when it goes null,
 * owns the per-patient Pusher subscription for AllergyListAllergyListChanged
 * (re-fetches on push), and exposes add/remove mutations.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientAllergiesStore {
  private patientStore = inject(PatientStore);
  private repo = inject(AllergiesRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Allergy[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.AllergyListAllergyListChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives, so
  // a fast patient switch can't end up showing the slower one's response.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  /**
   * Re-fetch allergies for the current patient. No-op if no current patient.
   */
  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Add an allergy to the current patient. Optimistic — appends to the list
   * immediately and rolls back on error.
   */
  add(allergy: Allergy): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientAllergiesStore.add() called with no current patient');
    }
    const previous = this._items();
    this._items.set([...previous, allergy]);
    return this.repo.addAllergyToPatient(patient.id, allergy).pipe(
      tap({
        next: () => this.reload(),
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  /**
   * Remove an allergy from the current patient by allergy id. Optimistic
   * remove with rollback on error.
   */
  remove(allergyId: number): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientAllergiesStore.remove() called with no current patient');
    }
    const previous = this._items();
    this._items.set(previous.filter(a => a.id !== allergyId));
    return this.repo.removeAllergyFromPatient(patient.id, allergyId).pipe(
      tap({
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientAllergyList(patientId).subscribe({
      next: allergies => {
        const sorted = [...allergies].sort((a, b) => (a.allergy > b.allergy ? 1 : -1));
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient allergy list');
        
      }
    });
  }
}
